import { Box, Button, ButtonProps, CircularProgress } from "@mui/material";
import { green, red } from "@mui/material/colors";
import { useMemo } from "react";

interface ButtonWithProgressProps extends Omit<ButtonProps, "disabled"> {
  loading: boolean;
  success?: boolean;
  error?: boolean;
  disabled?: boolean;
  progressSize?: number;
  progressColor?: string;
}

// UTILITIES
const BUTTON_STATUS_STYLES = {
  success: {
    bgcolor: green[500],
    "&:hover": { bgcolor: green[700] },
  },
  error: {
    bgcolor: red[500],
    "&:hover": { bgcolor: red[700] },
  },
  default: {},
};

/**
 * Genera gli stili per un bottone in base allo stato success/error
 * @param success - Indica se l'operazione è andata a buon fine
 * @param error - Indica se c'è stato un errore
 * @returns Oggetto con gli stili SxProps per MUI Button
 */
export const getButtonStatusSx = (success: boolean, error: boolean) => {
  if (success) return BUTTON_STATUS_STYLES.success;
  if (error) return BUTTON_STATUS_STYLES.error;
  return BUTTON_STATUS_STYLES.default;
};

/**
 * Button component with integrated loading indicator (CircularProgress)
 * Shows a centered spinner when loading is true
 * Optionally applies success/error styling
 */
export default function ButtonWithProgress({
  loading,
  success = false,
  error = false,
  disabled = false,
  progressSize = 24,
  progressColor = green[500],
  sx,
  children,
  ...buttonProps
}: ButtonWithProgressProps) {
  const statusSx = useMemo(
    () => getButtonStatusSx(success, error),
    [success, error],
  );

  return (
    <Box sx={{ m: 1, position: "relative" }}>
      <Button
        {...buttonProps}
        sx={{ ...statusSx, ...sx }}
        disabled={loading || disabled}
      >
        {children}
      </Button>
      {loading && (
        <CircularProgress
          size={progressSize}
          sx={{
            color: progressColor,
            position: "absolute",
            top: "50%",
            left: "50%",
            marginTop: `-${progressSize / 2}px`,
            marginLeft: `-${progressSize / 2}px`,
          }}
        />
      )}
    </Box>
  );
}
